import { useMutation } from "@apollo/client";
import { MenuItemProps, Text } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import DeleteConfirmMenuItem from "components/Shared/ConfirmModal/DeleteConfirmMenuItem";
import { removeDeletedRecords } from "utils/apollo/cacheHelpers";
import { assertPresence } from "utils/assertions";

const fragment = gql(/* GraphQL */ `
  fragment OrderingPlatformDeleteMenuItemFragment on OrderingPlatform {
    id
    name
    forBrandId
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation DeleteOrderingPlatform($orderingPlatformId: UUID!) {
    deleteOrderingPlatform(input: { id: $orderingPlatformId }) {
      orderingPlatform {
        id
        name
        ...OrderingPlatformDeleteMenuItemFragment
      }
    }
  }
`);

type Props = Pick<MenuItemProps, "isDisabled"> & {
  isAdmin?: boolean;
  onCompleted?: () => void;
  orderingPlatform: FragmentType<typeof fragment> | null | undefined;
};

export default function DeleteMenuItem({
  isAdmin,
  isDisabled,
  onCompleted,
  orderingPlatform: orderingPlatformFragment,
}: Props) {
  const orderingPlatform = getFragmentData(fragment, orderingPlatformFragment);

  const brandContext = useActiveBrandIdContext();

  const mutationTuple = useMutation(mutation, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    variables: orderingPlatform
      ? { orderingPlatformId: orderingPlatform.id }
      : undefined,
    update: (cache, result) => {
      const deletedOrderingPlatform =
        result.data?.deleteOrderingPlatform?.orderingPlatform;
      assertPresence(deletedOrderingPlatform);
      removeDeletedRecords({
        cache,
        baseFieldName: "orderingPlatforms",
        deletedRecords: [deletedOrderingPlatform],
      });
    },
    onCompleted: () => {
      onCompleted?.();
    },
  });

  const customLabel = orderingPlatform?.forBrandId ? "Custom" : "Default";

  return (
    <DeleteConfirmMenuItem
      label={`Delete ${customLabel} Customer…`}
      menuItemProps={{ isDisabled: isDisabled || !orderingPlatform }}
      mutation={mutationTuple}
      confirmText={
        <>
          <Text>
            Deleting an customer will permanently remove it from the directory,
            as well as any data associated with it. This cannot be undone and
            should always be used with caution. In most cases, archiving the
            customer is a better option. If this customer has already been used,
            including in sales plans or promotions, attempting to delete it will
            fail.
          </Text>
          <Text mt={3}>
            Are you sure you want to delete the {customLabel.toLowerCase()}{" "}
            customer, <strong>{orderingPlatform?.name}</strong>?
          </Text>
        </>
      }
    />
  );
}
